import { z } from 'zod';
import {
  addressSchema,
  baseRow,
  cents,
  customFieldValues,
  dateOnly,
  email,
  id,
  isoDate,
  listInput,
  nonEmpty,
  nonNegativeCents,
  optionalText,
  phone,
  quantity,
  signatureSchema,
  updateOf,
  url,
} from './common';
import { businessType } from './workspace';

// ---------------------------------------------------------------------------
// Enumerations
// ---------------------------------------------------------------------------

export const RELATIONSHIP_TYPES = [
  'client',
  'prospect',
  'subcontractor',
  'supplier',
  'architect_engineer',
  'inspector',
  'internal',
] as const;
export const relationshipType = z.enum(RELATIONSHIP_TYPES);
export type RelationshipType = z.infer<typeof relationshipType>;

export const PREQUAL_STATUSES = ['not_started', 'pending', 'approved', 'rejected', 'expired'] as const;
export const prequalStatus = z.enum(PREQUAL_STATUSES);

export const LEAD_SOURCES = [
  'referral',
  'website',
  'repeat_client',
  'advertising',
  'social',
  'home_show',
  'bid_invite',
  'rfp',
  'partner',
  'other',
] as const;
export const leadSource = z.enum(LEAD_SOURCES);
export type LeadSource = z.infer<typeof leadSource>;

export const PIPELINE_KINDS = ['homeowner_leads', 'bid_invites', 'rfp', 'gc_bids', 'general'] as const;
export const pipelineKind = z.enum(PIPELINE_KINDS);
export type PipelineKind = z.infer<typeof pipelineKind>;

export const STAGE_KINDS = ['open', 'won', 'lost'] as const;
export const stageKind = z.enum(STAGE_KINDS);
export type StageKind = z.infer<typeof stageKind>;

/**
 * Deal lifecycle status, independent of the pipeline stage: a deal is `open` while it moves
 * through the open stages, and `won`/`lost` once closed. Closing records `closed_at` plus the
 * won/lost reason; the stage it closed from stays on the deal (and in `deal_stage_history`).
 */
export const DEAL_STATUSES = ['open', 'won', 'lost'] as const;
export const dealStatus = z.enum(DEAL_STATUSES);
export type DealStatus = z.infer<typeof dealStatus>;

/** Where `deal.value_cents` comes from: typed in, or kept in sync with an estimate/proposal total. */
export const DEAL_AMOUNT_SOURCES = ['manual', 'estimate', 'proposal'] as const;
export const dealAmountSource = z.enum(DEAL_AMOUNT_SOURCES);
export type DealAmountSource = z.infer<typeof dealAmountSource>;

/** Deal fields a stage can require before a deal may enter it. */
export const DEAL_REQUIRABLE_FIELDS = [
  'contactId',
  'companyId',
  'ownerId',
  'valueCents',
  'expectedCloseDate',
  'source',
  'address',
  'bidDueAt',
] as const;
export const dealRequirableField = z.enum(DEAL_REQUIRABLE_FIELDS);
export type DealRequirableField = z.infer<typeof dealRequirableField>;

/** `stage.entry_requirements`: checked by the CRM module whenever a deal moves into the stage. */
export const stageEntryRequirementsSchema = z.object({
  requiredFields: z.array(dealRequirableField).max(DEAL_REQUIRABLE_FIELDS.length).default([]),
  /** At least one non-archived estimate linked to the deal. */
  requireEstimate: z.boolean().default(false),
  /** A proposal linked to the deal has been sent (status sent/viewed/signed). */
  requireSentProposal: z.boolean().default(false),
  /** A proposal linked to the deal has been signed. */
  requireSignedProposal: z.boolean().default(false),
});
export type StageEntryRequirements = z.infer<typeof stageEntryRequirementsSchema>;
export const DEFAULT_STAGE_ENTRY_REQUIREMENTS: StageEntryRequirements = {
  requiredFields: [],
  requireEstimate: false,
  requireSentProposal: false,
  requireSignedProposal: false,
};

/**
 * What happens to the deal when its proposal is signed:
 *  - `prompt` (default): task + notification for the deal owner and a "Proposal signed - mark as
 *    won?" banner on the deal with a one-click Won pre-filled with the signed total;
 *  - `mark_won`: close the deal as won automatically (signed total, reason "Proposal signed");
 *  - `move_to_stage`: move it to `stageId` (e.g. "Contract signed") and keep it open;
 *  - `none`: only log the signature.
 */
export const PROPOSAL_SIGNED_ACTIONS = ['prompt', 'mark_won', 'move_to_stage', 'none'] as const;
export const proposalSignedAction = z.enum(PROPOSAL_SIGNED_ACTIONS);
export type ProposalSignedAction = z.infer<typeof proposalSignedAction>;
/** What happens to the deal when its proposal is sent. */
export const PROPOSAL_SENT_ACTIONS = ['none', 'move_to_stage'] as const;
export const proposalSentAction = z.enum(PROPOSAL_SENT_ACTIONS);
export type ProposalSentAction = z.infer<typeof proposalSentAction>;
/** What happens to the deal when the client declines its proposal. */
export const PROPOSAL_DECLINED_ACTIONS = ['none', 'mark_lost'] as const;
export const proposalDeclinedAction = z.enum(PROPOSAL_DECLINED_ACTIONS);
export type ProposalDeclinedAction = z.infer<typeof proposalDeclinedAction>;
/**
 * After a deal is marked won: `prompt` offers "Convert to project" right away, `manual` only shows
 * the button on the deal. Conversion is never forced.
 */
export const DEAL_WON_CONVERSION_MODES = ['prompt', 'manual'] as const;
export const dealWonConversionMode = z.enum(DEAL_WON_CONVERSION_MODES);
export type DealWonConversionMode = z.infer<typeof dealWonConversionMode>;

/** Won reason recorded when a signed proposal closes (or prompts to close) a deal. */
export const PROPOSAL_SIGNED_WON_REASON = 'Proposal signed';

/**
 * `pipeline.settings`: per-pipeline automation and closing rules. Stored sparse; read it through
 * `pipelineSettingsSchema.parse(row.settings)` so every key gets its default.
 */
export const pipelineSettingsSchema = z.object({
  proposalSent: z
    .object({ action: proposalSentAction.default('none'), stageId: id.nullable().default(null) })
    .default({ action: 'none', stageId: null }),
  /** Default `prompt`: the owner confirms the win (one click), as in Pipedrive/HubSpot workflows. */
  proposalSigned: z
    .object({ action: proposalSignedAction.default('prompt'), stageId: id.nullable().default(null) })
    .default({ action: 'prompt', stageId: null }),
  proposalDeclined: z
    .object({ action: proposalDeclinedAction.default('none'), lostReasonId: id.nullable().default(null) })
    .default({ action: 'none', lostReasonId: null }),
  /** Rotting threshold for stages whose own `rotting_days` is null. Null disables it. */
  defaultRottingDays: z.number().int().min(1).max(365).nullable().default(null),
  requireLostReason: z.boolean().default(true),
  requireWonReason: z.boolean().default(false),
  /** Keep `deal.value_cents` in sync with the linked estimate/proposal total unless `amount_source` is manual. */
  syncAmountFromDocuments: z.boolean().default(true),
  wonConversion: dealWonConversionMode.default('prompt'),
});
export type PipelineSettings = z.infer<typeof pipelineSettingsSchema>;
export type PipelineSettingsInput = z.input<typeof pipelineSettingsSchema>;
export const DEFAULT_PIPELINE_SETTINGS: PipelineSettings = pipelineSettingsSchema.parse({});

export const ACTIVITY_TYPES = [
  'note',
  'call',
  'email',
  'meeting',
  'site_visit',
  'sms',
  'task_completed',
  'stage_change',
  'portal_action',
  'document',
  'system',
] as const;
export const activityType = z.enum(ACTIVITY_TYPES);
export type ActivityType = z.infer<typeof activityType>;

export const TASK_STATUSES = ['todo', 'in_progress', 'done', 'cancelled'] as const;
export const taskStatus = z.enum(TASK_STATUSES);
export type TaskStatus = z.infer<typeof taskStatus>;

export const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export const priority = z.enum(PRIORITIES);
export type Priority = z.infer<typeof priority>;

export const ESTIMATE_STATUSES = ['draft', 'in_review', 'sent', 'accepted', 'rejected', 'superseded'] as const;
export const estimateStatus = z.enum(ESTIMATE_STATUSES);
export type EstimateStatus = z.infer<typeof estimateStatus>;

export const COST_CATEGORIES = ['labor', 'material', 'equipment', 'subcontract', 'allowance', 'other'] as const;
export const costCategory = z.enum(COST_CATEGORIES);
export type CostCategory = z.infer<typeof costCategory>;

/** On-plan takeoff: lengths, areas and counts measured on a calibrated sheet. */
export const TAKEOFF_KINDS = ['linear', 'area', 'count'] as const;
export const takeoffKind = z.enum(TAKEOFF_KINDS);
export type TakeoffKind = z.infer<typeof takeoffKind>;

export const TAKEOFF_UNITS = ['ft', 'in', 'yd', 'm', 'mm'] as const;
export const takeoffUnit = z.enum(TAKEOFF_UNITS);
export type TakeoffUnit = z.infer<typeof takeoffUnit>;

/** Sheet scale: real-world units per image pixel (page rendered at scale 1). */
export const takeoffScale = z.object({
  unitsPerPixel: z.number().positive(),
  unit: takeoffUnit,
  /** Human label, e.g. 1/4" = 1'-0" or "calibrated". */
  label: z.string().max(60).optional(),
});
export type TakeoffScale = z.infer<typeof takeoffScale>;

/** One or more shapes in sheet pixel coordinates: a polyline (linear), polygon (area) or points (count). */
export const takeoffGeometry = z
  .array(z.array(z.object({ x: z.number(), y: z.number() })).max(2000))
  .max(500);
export type TakeoffGeometry = z.infer<typeof takeoffGeometry>;

export const PROPOSAL_STATUSES = ['draft', 'sent', 'viewed', 'signed', 'declined', 'expired', 'void'] as const;
export const proposalStatus = z.enum(PROPOSAL_STATUSES);
export type ProposalStatus = z.infer<typeof proposalStatus>;

export const TEMPLATE_KINDS = ['email', 'sms', 'proposal', 'note'] as const;
export const templateKind = z.enum(TEMPLATE_KINDS);
export type TemplateKind = z.infer<typeof templateKind>;

/**
 * Update input: like `updateOf` (`{ id, expectedVersion?, ...partial }`) but WITHOUT the create
 * schema's defaults. zod 4's `.partial()` still applies `.default()`s, so `updateOf` would turn
 * `{ id, jobTitle }` into an update that also resets tags, emails, lines, ... to their defaults.
 */
function patchOf<T extends z.ZodRawShape>(create: z.ZodObject<T>): ReturnType<typeof updateOf<T>> {
  const shape: Record<string, z.ZodType> = {};
  for (const [key, field] of Object.entries(create.shape as unknown as Record<string, z.ZodType>)) {
    let inner: z.ZodType = field;
    while (inner instanceof z.ZodDefault) inner = inner.removeDefault() as z.ZodType;
    shape[key] = inner.optional();
  }
  return updateOf(z.object(shape)) as unknown as ReturnType<typeof updateOf<T>>;
}

const emailEntry = z.object({ email, label: z.string().max(40).default('work'), primary: z.boolean().default(false) });
const phoneEntry = z.object({ phone, label: z.string().max(40).default('mobile'), primary: z.boolean().default(false) });

// ---------------------------------------------------------------------------
// Company
// ---------------------------------------------------------------------------

export const createCompanyInput = z.object({
  name: nonEmpty(200),
  relationshipTypes: z.array(relationshipType).min(1).default(['client']),
  domain: z.string().trim().toLowerCase().max(253).optional().nullable(),
  website: url.optional().nullable(),
  phone: phone.optional().nullable(),
  email: email.optional().nullable(),
  address: addressSchema.optional().nullable(),
  trades: z.array(z.string().trim().min(1).max(80)).max(50).default([]),
  licenseNumber: optionalText(120),
  insuranceExpiresAt: dateOnly.optional().nullable(),
  workersCompExpiresAt: dateOnly.optional().nullable(),
  prequalStatus: prequalStatus.default('not_started'),
  rating: z.number().int().min(1).max(5).optional().nullable(),
  notes: optionalText(),
  tags: z.array(z.string().trim().min(1).max(60)).max(50).default([]),
  ownerId: id.optional().nullable(),
  custom: customFieldValues.default({}),
});
export type CreateCompanyInput = z.infer<typeof createCompanyInput>;
export const updateCompanyInput = patchOf(createCompanyInput);
export type UpdateCompanyInput = z.infer<typeof updateCompanyInput>;

export const companySchema = baseRow.extend({
  name: z.string(),
  relationshipTypes: z.array(relationshipType),
  domain: z.string().nullable(),
  website: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  address: addressSchema.nullable(),
  trades: z.array(z.string()),
  licenseNumber: z.string().nullable(),
  insuranceExpiresAt: z.string().nullable(),
  workersCompExpiresAt: z.string().nullable(),
  prequalStatus,
  rating: z.number().int().nullable(),
  notes: z.string().nullable(),
  tags: z.array(z.string()),
  ownerId: id.nullable(),
  custom: customFieldValues,
});
export type Company = z.infer<typeof companySchema>;

export const listCompaniesInput = listInput.extend({
  relationshipType: relationshipType.optional(),
  trade: z.string().max(80).optional(),
  prequalStatus: prequalStatus.optional(),
  insuranceExpiringBefore: dateOnly.optional(),
  tag: z.string().max(60).optional(),
});
export type ListCompaniesInput = z.infer<typeof listCompaniesInput>;

// ---------------------------------------------------------------------------
// Contact
// ---------------------------------------------------------------------------

export const createContactInput = z.object({
  firstName: z.string().trim().max(120).default(''),
  lastName: z.string().trim().max(120).default(''),
  displayName: z.string().trim().max(240).optional(),
  companyId: id.optional().nullable(),
  jobTitle: optionalText(120),
  relationshipTypes: z.array(relationshipType).min(1).default(['prospect']),
  emails: z.array(emailEntry).max(10).default([]),
  phones: z.array(phoneEntry).max(10).default([]),
  address: addressSchema.optional().nullable(),
  source: leadSource.optional().nullable(),
  tags: z.array(z.string().trim().min(1).max(60)).max(50).default([]),
  notes: optionalText(),
  ownerId: id.optional().nullable(),
  portalEnabled: z.boolean().default(false),
  custom: customFieldValues.default({}),
});
export type CreateContactInput = z.infer<typeof createContactInput>;
export const updateContactInput = patchOf(createContactInput);
export type UpdateContactInput = z.infer<typeof updateContactInput>;

export const contactSchema = baseRow.extend({
  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string(),
  companyId: id.nullable(),
  jobTitle: z.string().nullable(),
  relationshipTypes: z.array(relationshipType),
  emails: z.array(z.object({ email: z.string(), label: z.string(), primary: z.boolean() })),
  phones: z.array(z.object({ phone: z.string(), label: z.string(), primary: z.boolean() })),
  primaryEmail: z.string().nullable(),
  primaryPhone: z.string().nullable(),
  address: addressSchema.nullable(),
  source: leadSource.nullable(),
  tags: z.array(z.string()),
  notes: z.string().nullable(),
  ownerId: id.nullable(),
  portalEnabled: z.boolean(),
  mergedIntoId: id.nullable(),
  custom: customFieldValues,
});
export type Contact = z.infer<typeof contactSchema>;

export const listContactsInput = listInput.extend({
  relationshipType: relationshipType.optional(),
  companyId: id.optional(),
  ownerId: id.optional(),
  tag: z.string().max(60).optional(),
  portalEnabled: z.boolean().optional(),
});
export type ListContactsInput = z.infer<typeof listContactsInput>;

export const findDuplicatesInput = z.object({
  email: z.string().max(320).optional(),
  phone: z.string().max(40).optional(),
  domain: z.string().max(253).optional(),
  excludeId: id.optional(),
});
export type FindDuplicatesInput = z.infer<typeof findDuplicatesInput>;

export const mergeContactsInput = z.object({
  survivorId: id,
  mergedIds: z.array(id).min(1).max(20),
  /** Field-by-field choice of which record wins; missing keys keep survivor's value. */
  fieldSources: z.record(z.string(), id).default({}),
});
export type MergeContactsInput = z.infer<typeof mergeContactsInput>;

export const mergeCompaniesInput = mergeContactsInput;

export const csvImportInput = z.object({
  entity: z.enum(['contact', 'company']),
  /** R2 key of the uploaded CSV, or raw CSV text for small files (<1MB). */
  fileKey: z.string().max(1024).optional(),
  csv: z.string().max(1_000_000).optional(),
  /** Map CSV header -> field path (e.g. `firstName`, `emails.0.email`, `custom.lot_number`). */
  mapping: z.record(z.string(), z.string()),
  dedupe: z.enum(['skip', 'update', 'create']).default('skip'),
  defaultRelationshipType: relationshipType.default('prospect'),
});
export type CsvImportInput = z.infer<typeof csvImportInput>;

export const csvImportResult = z.object({
  created: z.number().int(),
  updated: z.number().int(),
  skipped: z.number().int(),
  errors: z.array(z.object({ row: z.number().int(), message: z.string() })),
});
export type CsvImportResult = z.infer<typeof csvImportResult>;

// ---------------------------------------------------------------------------
// Pipelines, stages, deals
// ---------------------------------------------------------------------------

export const createStageInput = z.object({
  name: nonEmpty(80),
  kind: stageKind.default('open'),
  position: z.number().int().min(0),
  probabilityBps: z.number().int().min(0).max(10_000).default(0),
  color: z.string().max(16).optional().nullable(),
  /** Days in stage before the deal is flagged as stale. */
  rottingDays: z.number().int().min(0).max(365).optional().nullable(),
  entryRequirements: stageEntryRequirementsSchema.optional(),
});
export type CreateStageInput = z.infer<typeof createStageInput>;
export const updateStageInput = patchOf(createStageInput);
export type UpdateStageInput = z.infer<typeof updateStageInput>;

export const stageSchema = baseRow.extend({
  pipelineId: id,
  name: z.string(),
  kind: stageKind,
  position: z.number().int(),
  probabilityBps: z.number().int(),
  color: z.string().nullable(),
  rottingDays: z.number().int().nullable(),
  entryRequirements: stageEntryRequirementsSchema,
});
export type Stage = z.infer<typeof stageSchema>;

export const createPipelineInput = z.object({
  name: nonEmpty(120),
  kind: pipelineKind.default('general'),
  businessType: businessType.optional().nullable(),
  isDefault: z.boolean().default(false),
  settings: pipelineSettingsSchema.optional(),
  stages: z.array(createStageInput).min(2).max(30).optional(),
});
export type CreatePipelineInput = z.infer<typeof createPipelineInput>;
export const updatePipelineInput = patchOf(createPipelineInput.omit({ stages: true }));
export type UpdatePipelineInput = z.infer<typeof updatePipelineInput>;

export const pipelineSchema = baseRow.extend({
  name: z.string(),
  kind: pipelineKind,
  businessType: businessType.nullable(),
  isDefault: z.boolean(),
  position: z.number().int(),
  /** Parsed with defaults applied (the column itself is stored sparse). */
  settings: pipelineSettingsSchema,
});
export type Pipeline = z.infer<typeof pipelineSchema>;
export const pipelineWithStagesSchema = pipelineSchema.extend({ stages: z.array(stageSchema) });
export type PipelineWithStages = z.infer<typeof pipelineWithStagesSchema>;

export const reorderStagesInput = z.object({ pipelineId: id, stageIds: z.array(id).min(1) });

export const createDealInput = z.object({
  title: nonEmpty(200),
  pipelineId: id,
  stageId: id,
  contactId: id.optional().nullable(),
  companyId: id.optional().nullable(),
  ownerId: id.optional().nullable(),
  valueCents: nonNegativeCents.default(0),
  source: leadSource.optional().nullable(),
  expectedCloseDate: dateOnly.optional().nullable(),
  address: addressSchema.optional().nullable(),
  description: optionalText(),
  /** Commercial: bid due date / RFP due. */
  bidDueAt: isoDate.optional().nullable(),
  tags: z.array(z.string().trim().min(1).max(60)).max(50).default([]),
  custom: customFieldValues.default({}),
});
export type CreateDealInput = z.infer<typeof createDealInput>;
export const updateDealInput = patchOf(createDealInput);
export type UpdateDealInput = z.infer<typeof updateDealInput>;

export const dealSchema = baseRow.extend({
  title: z.string(),
  pipelineId: id,
  stageId: id,
  status: dealStatus,
  contactId: id.nullable(),
  companyId: id.nullable(),
  ownerId: id.nullable(),
  valueCents: cents,
  source: leadSource.nullable(),
  expectedCloseDate: z.string().nullable(),
  address: addressSchema.nullable(),
  description: z.string().nullable(),
  bidDueAt: z.date().nullable(),
  stageEnteredAt: z.date(),
  wonAt: z.date().nullable(),
  lostAt: z.date().nullable(),
  /** Legacy free-text lost reason; new code writes `lostReasonId` + `lostReasonNote`. */
  lostReason: z.string().nullable(),
  projectId: id.nullable(),
  tags: z.array(z.string()),
  custom: customFieldValues,
  /** Overrides the stage's probability for forecasting; null uses the stage's. */
  probabilityOverrideBps: z.number().int().nullable(),
  /** When the deal was won or lost; null while open. */
  closedAt: z.date().nullable(),
  wonReasonId: id.nullable(),
  wonReason: z.string().nullable(),
  lostReasonId: id.nullable(),
  lostReasonNote: z.string().nullable(),
  lostToCompetitor: z.string().nullable(),
  amountSource: dealAmountSource,
  /** Estimate or proposal id the amount is synced from (when `amountSource` is not manual). */
  amountSourceId: id.nullable(),
  /** Last logged activity (calls, emails, meetings, notes); drives rotting together with `stageEnteredAt`. */
  lastActivityAt: z.date().nullable(),
});
export type Deal = z.infer<typeof dealSchema>;

// ---------------------------------------------------------------------------
// Deal lifecycle: close as won/lost, reopen, reasons, stage history
// ---------------------------------------------------------------------------

export const markDealWonInput = z.object({
  id,
  wonReasonId: id.optional().nullable(),
  /** Free-text won note (required when the chosen reason `requiresNote`). */
  wonReason: optionalText(2000),
  /** Close date in the workspace time zone (default today). Takes precedence over `closedAt`. */
  closeDate: dateOnly.optional(),
  /** Exact close timestamp (API clients); defaults to now. */
  closedAt: isoDate.optional(),
  /** Final contract amount (defaults to the accepted proposal/estimate total, else the current value). */
  valueCents: nonNegativeCents.optional(),
  /** Move to this won stage of the pipeline (defaults to the pipeline's first won stage). */
  stageId: id.optional(),
  expectedVersion: z.number().int().optional(),
});
export type MarkDealWonInput = z.infer<typeof markDealWonInput>;

export const markDealLostInput = z.object({
  id,
  /** Required when the pipeline's `requireLostReason` is on (the default). */
  lostReasonId: id.optional().nullable(),
  lostReasonNote: optionalText(2000),
  lostToCompetitor: z.string().trim().max(200).optional().nullable(),
  closeDate: dateOnly.optional(),
  closedAt: isoDate.optional(),
  /** Move to this lost stage of the pipeline (defaults to the pipeline's first lost stage). */
  stageId: id.optional(),
  expectedVersion: z.number().int().optional(),
});
export type MarkDealLostInput = z.infer<typeof markDealLostInput>;

export const reopenDealInput = z.object({
  id,
  /** Open stage to reopen into (defaults to the last open stage the deal was in). */
  stageId: id.optional(),
  expectedVersion: z.number().int().optional(),
});
export type ReopenDealInput = z.infer<typeof reopenDealInput>;

/** Dismiss the "Proposal signed - mark as won?" banner (cancels the owner's follow-up task). */
export const dismissWonPromptInput = z.object({ id });

export const setDealProbabilityInput = z.object({
  id,
  probabilityOverrideBps: z.number().int().min(0).max(10_000).nullable(),
  expectedVersion: z.number().int().optional(),
});
export type SetDealProbabilityInput = z.infer<typeof setDealProbabilityInput>;

export const setDealAmountSourceInput = z.object({
  id,
  amountSource: dealAmountSource,
  amountSourceId: id.optional().nullable(),
  expectedVersion: z.number().int().optional(),
});
export type SetDealAmountSourceInput = z.infer<typeof setDealAmountSourceInput>;

export const updatePipelineSettingsInput = z.object({
  pipelineId: id,
  settings: pipelineSettingsSchema,
  expectedVersion: z.number().int().optional(),
});
export type UpdatePipelineSettingsInput = z.infer<typeof updatePipelineSettingsInput>;

export const setStageEntryRequirementsInput = z.object({
  stageId: id,
  entryRequirements: stageEntryRequirementsSchema,
  expectedVersion: z.number().int().optional(),
});

/** Workspace-configurable won / lost reasons (`deal_won_reason`, `deal_lost_reason`). */
export const createDealReasonInput = z.object({
  label: nonEmpty(120),
  description: optionalText(500),
  position: z.number().int().min(0).default(0),
  /** The user must add a note when choosing this reason (e.g. "Other"). */
  requiresNote: z.boolean().default(false),
});
export type CreateDealReasonInput = z.infer<typeof createDealReasonInput>;
export const updateDealReasonInput = patchOf(createDealReasonInput);
export type UpdateDealReasonInput = z.infer<typeof updateDealReasonInput>;
export const dealReasonSchema = baseRow.extend({
  label: z.string(),
  description: z.string().nullable(),
  position: z.number().int(),
  requiresNote: z.boolean(),
});
export type DealReason = z.infer<typeof dealReasonSchema>;
export const dealLostReasonSchema = dealReasonSchema;
export type DealLostReason = DealReason;
export const dealWonReasonSchema = dealReasonSchema;
export type DealWonReason = DealReason;
export const DEAL_REASON_KINDS = ['won', 'lost'] as const;
export const dealReasonKind = z.enum(DEAL_REASON_KINDS);
export type DealReasonKind = z.infer<typeof dealReasonKind>;
export const reorderDealReasonsInput = z.object({ kind: dealReasonKind, ids: z.array(id).min(1).max(200) });
export const listDealReasonsInput = z.object({ kind: dealReasonKind, includeArchived: z.boolean().default(false) });
export const dealReasonKindInput = z.object({ kind: dealReasonKind });

/** Seeded into every new workspace by `provisionWorkspace`. */
export const DEFAULT_DEAL_LOST_REASONS: readonly { label: string; requiresNote: boolean }[] = [
  { label: 'Price too high', requiresNote: false },
  { label: 'Went with a competitor', requiresNote: false },
  { label: 'Budget not approved / financing fell through', requiresNote: false },
  { label: 'Project postponed or cancelled', requiresNote: false },
  { label: 'Scope outside our services', requiresNote: false },
  { label: 'Schedule did not work', requiresNote: false },
  { label: 'No response from client', requiresNote: false },
  { label: 'Not qualified', requiresNote: false },
  { label: 'Other', requiresNote: true },
];
export const DEFAULT_DEAL_WON_REASONS: readonly { label: string; requiresNote: boolean }[] = [
  { label: 'Price / value', requiresNote: false },
  { label: 'Quality and reputation', requiresNote: false },
  { label: 'Referral or existing relationship', requiresNote: false },
  { label: 'Design and scope', requiresNote: false },
  { label: 'Schedule / availability', requiresNote: false },
  { label: 'Other', requiresNote: true },
];

/** One row per stage/status change of a deal (`deal_stage_history`). */
export const dealStageHistorySchema = baseRow.extend({
  dealId: id,
  pipelineId: id,
  /** Null on the row recorded when the deal is created. */
  fromStageId: id.nullable(),
  toStageId: id.nullable(),
  fromStatus: dealStatus.nullable(),
  toStatus: dealStatus,
  changedBy: id.nullable(),
  changedAt: z.date(),
  /** Seconds the deal spent in `fromStageId` (null on the creation row). */
  durationSeconds: z.number().int().nullable(),
  /** Deal value at the time of the change (for forecast-accuracy and velocity reports). */
  valueCents: cents,
});
export type DealStageHistory = z.infer<typeof dealStageHistorySchema>;
export const listDealStageHistoryInput = z.object({ dealId: id });

export const listDealsInput = listInput.extend({
  pipelineId: id.optional(),
  stageId: id.optional(),
  status: dealStatus.optional(),
  ownerId: id.optional(),
  contactId: id.optional(),
  companyId: id.optional(),
  source: leadSource.optional(),
});
export type ListDealsInput = z.infer<typeof listDealsInput>;

export const moveDealInput = z.object({
  id,
  stageId: id,
  /** Moving into a Lost stage closes the deal as lost: pass a reason id (or a reason's label). */
  lostReasonId: id.optional().nullable(),
  lostReason: optionalText(500),
  expectedVersion: z.number().int().optional(),
});
export type MoveDealInput = z.infer<typeof moveDealInput>;

export const convertDealToProjectInput = z.object({
  dealId: id,
  projectName: nonEmpty(200).optional(),
  projectTemplateId: id.optional().nullable(),
  estimateId: id.optional().nullable(),
  startDate: dateOnly.optional().nullable(),
  pmId: id.optional().nullable(),
  copyDocuments: z.boolean().default(true),
  enablePortal: z.boolean().default(false),
});
export type ConvertDealToProjectInput = z.infer<typeof convertDealToProjectInput>;

// ---------------------------------------------------------------------------
// Activities + tasks
// ---------------------------------------------------------------------------

export const createActivityInput = z.object({
  type: activityType,
  subject: z.string().trim().max(300).optional().nullable(),
  body: optionalText(50_000),
  occurredAt: isoDate.optional(),
  durationMinutes: z.number().int().min(0).max(24 * 60).optional().nullable(),
  contactId: id.optional().nullable(),
  companyId: id.optional().nullable(),
  dealId: id.optional().nullable(),
  projectId: id.optional().nullable(),
  metadata: z.record(z.string(), z.unknown()).default({}),
});
export type CreateActivityInput = z.infer<typeof createActivityInput>;
export const updateActivityInput = patchOf(createActivityInput.omit({ type: true }));
export type UpdateActivityInput = z.infer<typeof updateActivityInput>;

export const activitySchema = baseRow.extend({
  type: activityType,
  subject: z.string().nullable(),
  body: z.string().nullable(),
  occurredAt: z.date(),
  durationMinutes: z.number().int().nullable(),
  actorId: id.nullable(),
  contactId: id.nullable(),
  companyId: id.nullable(),
  dealId: id.nullable(),
  projectId: id.nullable(),
  emailMessageId: id.nullable(),
  metadata: z.record(z.string(), z.unknown()),
});
export type Activity = z.infer<typeof activitySchema>;

export const listActivitiesInput = listInput.extend({
  contactId: id.optional(),
  companyId: id.optional(),
  dealId: id.optional(),
  projectId: id.optional(),
  type: activityType.optional(),
});
export type ListActivitiesInput = z.infer<typeof listActivitiesInput>;

export const createTaskInput = z.object({
  title: nonEmpty(300),
  description: optionalText(),
  status: taskStatus.default('todo'),
  priority: priority.default('normal'),
  dueAt: isoDate.optional().nullable(),
  remindAt: isoDate.optional().nullable(),
  assigneeId: id.optional().nullable(),
  contactId: id.optional().nullable(),
  companyId: id.optional().nullable(),
  dealId: id.optional().nullable(),
  projectId: id.optional().nullable(),
});
export type CreateTaskInput = z.infer<typeof createTaskInput>;
export const updateTaskInput = patchOf(createTaskInput);
export type UpdateTaskInput = z.infer<typeof updateTaskInput>;

export const taskSchema = baseRow.extend({
  title: z.string(),
  description: z.string().nullable(),
  status: taskStatus,
  priority,
  dueAt: z.date().nullable(),
  remindAt: z.date().nullable(),
  completedAt: z.date().nullable(),
  assigneeId: id.nullable(),
  contactId: id.nullable(),
  companyId: id.nullable(),
  dealId: id.nullable(),
  projectId: id.nullable(),
});
export type Task = z.infer<typeof taskSchema>;

export const listTasksInput = listInput.extend({
  assigneeId: id.optional(),
  status: taskStatus.optional(),
  dueBefore: isoDate.optional(),
  dealId: id.optional(),
  projectId: id.optional(),
  contactId: id.optional(),
  /** "My day": tasks assigned to the caller that are overdue or due today. */
  myDay: z.boolean().default(false),
});
export type ListTasksInput = z.infer<typeof listTasksInput>;

// ---------------------------------------------------------------------------
// Estimates + proposals
// ---------------------------------------------------------------------------

export const estimateLineInput = z.object({
  id: id.optional(),
  position: z.number().int().min(0),
  section: z.string().trim().max(120).optional().nullable(),
  costCode: z.string().trim().max(40).optional().nullable(),
  category: costCategory.default('material'),
  description: nonEmpty(1000),
  quantity: quantity.default(1),
  unit: z.string().trim().max(20).default('ea'),
  unitCostCents: cents.default(0),
  markupBps: z.number().int().min(-10_000).max(100_000).optional().nullable(),
  taxable: z.boolean().default(true),
  isAllowance: z.boolean().default(false),
  isOptional: z.boolean().default(false),
  notes: optionalText(2000),
});
export type EstimateLineInput = z.infer<typeof estimateLineInput>;

export const estimateLineSchema = baseRow.extend({
  estimateId: id,
  position: z.number().int(),
  section: z.string().nullable(),
  costCode: z.string().nullable(),
  category: costCategory,
  description: z.string(),
  quantity: z.number(),
  unit: z.string(),
  unitCostCents: cents,
  markupBps: z.number().int().nullable(),
  costTotalCents: cents,
  priceTotalCents: cents,
  taxable: z.boolean(),
  isAllowance: z.boolean(),
  isOptional: z.boolean(),
  notes: z.string().nullable(),
});
export type EstimateLine = z.infer<typeof estimateLineSchema>;

export const createEstimateInput = z.object({
  title: nonEmpty(200),
  dealId: id.optional().nullable(),
  projectId: id.optional().nullable(),
  contactId: id.optional().nullable(),
  markupBps: z.number().int().min(0).max(100_000).optional(),
  taxRateBps: z.number().int().min(0).max(10_000).optional(),
  validUntil: dateOnly.optional().nullable(),
  notes: optionalText(),
  terms: optionalText(),
  lines: z.array(estimateLineInput).max(2000).default([]),
  custom: customFieldValues.default({}),
  /** Start from a saved estimate template: its lines (when none are given), markup, validity, notes and terms. */
  estimateTemplateId: id.optional().nullable(),
});
export type CreateEstimateInput = z.infer<typeof createEstimateInput>;
export const updateEstimateInput = patchOf(createEstimateInput.omit({ estimateTemplateId: true })).extend({ status: estimateStatus.optional() });
export type UpdateEstimateInput = z.infer<typeof updateEstimateInput>;

export const estimateSchema = baseRow.extend({
  number: z.string(),
  title: z.string(),
  status: estimateStatus,
  dealId: id.nullable(),
  projectId: id.nullable(),
  contactId: id.nullable(),
  markupBps: z.number().int(),
  taxRateBps: z.number().int(),
  costSubtotalCents: cents,
  subtotalCents: cents,
  taxCents: cents,
  totalCents: cents,
  validUntil: z.string().nullable(),
  notes: z.string().nullable(),
  terms: z.string().nullable(),
  custom: customFieldValues,
});
export type Estimate = z.infer<typeof estimateSchema>;
export const estimateWithLinesSchema = estimateSchema.extend({ lines: z.array(estimateLineSchema) });
export type EstimateWithLines = z.infer<typeof estimateWithLinesSchema>;

export const listEstimatesInput = listInput.extend({
  dealId: id.optional(),
  projectId: id.optional(),
  status: estimateStatus.optional(),
});

export const createProposalInput = z.object({
  title: nonEmpty(200),
  estimateId: id.optional().nullable(),
  dealId: id.optional().nullable(),
  contactId: id,
  /** Rich text (HTML, sanitized server-side) for cover letter / scope. */
  body: z.string().max(200_000).default(''),
  expiresAt: isoDate.optional().nullable(),
  depositCents: nonNegativeCents.optional().nullable(),
  templateId: id.optional().nullable(),
});
export type CreateProposalInput = z.infer<typeof createProposalInput>;
/** Attach an estimate or proposal made outside the pipeline to an open deal. */
export const linkToDealInput = z.object({ id, dealId: id });

export const updateProposalInput = patchOf(createProposalInput);
export type UpdateProposalInput = z.infer<typeof updateProposalInput>;

export const proposalSchema = baseRow.extend({
  number: z.string(),
  title: z.string(),
  status: proposalStatus,
  estimateId: id.nullable(),
  dealId: id.nullable(),
  contactId: id,
  body: z.string(),
  totalCents: cents,
  depositCents: cents.nullable(),
  expiresAt: z.date().nullable(),
  sentAt: z.date().nullable(),
  viewedAt: z.date().nullable(),
  signedAt: z.date().nullable(),
  declinedAt: z.date().nullable(),
  declineReason: z.string().nullable(),
  signature: signatureSchema.nullable(),
  documentId: id.nullable(),
  publicToken: z.string().nullable(),
});
export type Proposal = z.infer<typeof proposalSchema>;

export const sendProposalInput = z.object({
  id,
  to: z.array(email).min(1).max(10),
  subject: nonEmpty(300),
  message: optionalText(10_000),
});
export const signProposalInput = z.object({ token: z.string().min(16).max(256), signature: signatureSchema });
export const declineProposalInput = z.object({ token: z.string().min(16).max(256), reason: optionalText(2000) });
export const listProposalsInput = listInput.extend({
  dealId: id.optional(),
  contactId: id.optional(),
  status: proposalStatus.optional(),
});

// ---------------------------------------------------------------------------
// Templates (email / sms / proposal)
// ---------------------------------------------------------------------------

export const createTemplateInput = z.object({
  kind: templateKind,
  name: nonEmpty(120),
  subject: z.string().trim().max(300).optional().nullable(),
  /** Supports {{contact.firstName}}, {{deal.title}}, {{workspace.name}} merge tags. */
  body: z.string().max(200_000),
  shared: z.boolean().default(true),
});
export type CreateTemplateInput = z.infer<typeof createTemplateInput>;
export const updateTemplateInput = patchOf(createTemplateInput);
export const templateSchema = baseRow.extend({
  kind: templateKind,
  name: z.string(),
  subject: z.string().nullable(),
  body: z.string(),
  shared: z.boolean(),
  ownerId: id.nullable(),
});
export type Template = z.infer<typeof templateSchema>;

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export const reportRangeInput = z.object({
  from: dateOnly,
  to: dateOnly,
  pipelineId: id.optional(),
  ownerId: id.optional(),
});
export type ReportRangeInput = z.infer<typeof reportRangeInput>;

export const pipelineByStageReport = z.array(
  z.object({ stageId: id, stageName: z.string(), count: z.number().int(), valueCents: cents, weightedCents: cents }),
);
export const pipelineByMonthReport = z.array(
  z.object({ month: z.string(), created: z.number().int(), won: z.number().int(), lost: z.number().int(), wonValueCents: cents }),
);
export const winRateReport = z.object({
  won: z.number().int(),
  lost: z.number().int(),
  winRateBps: z.number().int(),
  avgDaysToClose: z.number().nullable(),
  avgWonValueCents: cents,
});
export const sourceReport = z.array(
  z.object({ source: z.string(), deals: z.number().int(), won: z.number().int(), wonValueCents: cents }),
);
export const clientLtvReport = z.array(
  z.object({ contactId: id.nullable(), companyId: id.nullable(), name: z.string(), projects: z.number().int(), lifetimeValueCents: cents }),
);

// ===========================================================================
// CRM module additions (vendor management, saved views, CSV, tasks, email, reports).
// Names are CRM-specific because every contracts file is re-exported flat.
// ===========================================================================

// ---------------------------------------------------------------------------
// Sub / vendor management
// ---------------------------------------------------------------------------

/** CSI MasterFormat (2018) divisions used to classify subs and suppliers. */
export const VENDOR_CSI_DIVISIONS = [
  { code: '01', name: 'General Requirements' },
  { code: '02', name: 'Existing Conditions' },
  { code: '03', name: 'Concrete' },
  { code: '04', name: 'Masonry' },
  { code: '05', name: 'Metals' },
  { code: '06', name: 'Wood, Plastics, and Composites' },
  { code: '07', name: 'Thermal and Moisture Protection' },
  { code: '08', name: 'Openings' },
  { code: '09', name: 'Finishes' },
  { code: '10', name: 'Specialties' },
  { code: '11', name: 'Equipment' },
  { code: '12', name: 'Furnishings' },
  { code: '13', name: 'Special Construction' },
  { code: '14', name: 'Conveying Equipment' },
  { code: '21', name: 'Fire Suppression' },
  { code: '22', name: 'Plumbing' },
  { code: '23', name: 'HVAC' },
  { code: '25', name: 'Integrated Automation' },
  { code: '26', name: 'Electrical' },
  { code: '27', name: 'Communications' },
  { code: '28', name: 'Electronic Safety and Security' },
  { code: '31', name: 'Earthwork' },
  { code: '32', name: 'Exterior Improvements' },
  { code: '33', name: 'Utilities' },
  { code: '34', name: 'Transportation' },
  { code: '35', name: 'Waterway and Marine Construction' },
] as const;
export const vendorCsiDivision = z.string().regex(/^\d{2}$/, 'Two-digit CSI division');

export const INSURANCE_POLICY_TYPES = [
  'general_liability',
  'workers_comp',
  'auto',
  'umbrella',
  'professional',
  'builders_risk',
  'pollution',
  'other',
] as const;
export const insurancePolicyType = z.enum(INSURANCE_POLICY_TYPES);
export type InsurancePolicyType = z.infer<typeof insurancePolicyType>;

export const vendorInsuranceCertificateSchema = z.object({
  id,
  policyType: insurancePolicyType,
  carrier: z.string().trim().max(200).default(''),
  policyNumber: z.string().trim().max(120).default(''),
  /** Per-occurrence limit in cents. */
  coverageCents: nonNegativeCents.nullable().default(null),
  effectiveDate: dateOnly.nullable().default(null),
  expiresAt: dateOnly,
  additionalInsured: z.boolean().default(false),
  /** `document` row holding the uploaded COI (folder `insurance`, linked to the company). */
  documentId: id.nullable().default(null),
  notes: z.string().max(2000).nullable().default(null),
});
export type VendorInsuranceCertificate = z.infer<typeof vendorInsuranceCertificateSchema>;

export const vendorLicenseSchema = z.object({
  id,
  kind: z.string().trim().min(1).max(120),
  number: z.string().trim().min(1).max(120),
  region: z.string().trim().max(120).default(''),
  expiresAt: dateOnly.nullable().default(null),
});
export type VendorLicense = z.infer<typeof vendorLicenseSchema>;

export const vendorPrequalAnswerSchema = z.object({
  questionId: z.string().min(1).max(64),
  question: z.string().max(500),
  answer: z.string().max(5000),
});
export type VendorPrequalAnswer = z.infer<typeof vendorPrequalAnswerSchema>;

export const vendorServiceAreaSchema = z.object({
  regions: z.array(z.string().trim().min(1).max(120)).max(100).default([]),
  postalCodes: z.array(z.string().trim().min(1).max(20)).max(500).default([]),
  radiusMiles: z.number().int().min(0).max(2000).nullable().default(null),
});
export type VendorServiceArea = z.infer<typeof vendorServiceAreaSchema>;

/**
 * Editable vendor profile data, as stored in `vendor_profile` (one row per sub/supplier company).
 * Ratings live in `vendor_rating`; the denormalized expiry/rating columns are derived.
 */
export const vendorProfileSchema = z.object({
  trades: z.array(z.string().trim().min(1).max(80)).max(50).default([]),
  csiDivisions: z.array(vendorCsiDivision).max(50).default([]),
  serviceArea: vendorServiceAreaSchema.default({ regions: [], postalCodes: [], radiusMiles: null }),
  licenses: z.array(vendorLicenseSchema).max(50).default([]),
  insuranceCertificates: z.array(vendorInsuranceCertificateSchema).max(50).default([]),
  prequalAnswers: z.array(vendorPrequalAnswerSchema).max(100).default([]),
  prequalSubmittedAt: z.date().nullable().default(null),
  prequalReviewedAt: z.date().nullable().default(null),
  prequalReviewedBy: id.nullable().default(null),
  prequalNote: z.string().nullable().default(null),
  /** Last expiry alert sent, per certificate id (`"<status>:<expiresAt>"`, prevents repeat alerts). */
  alertedCertificates: z.record(z.string(), z.string()).default({}),
});
export type VendorProfile = z.infer<typeof vendorProfileSchema>;

/** Default prequalification questionnaire (workspaces answer these per sub/vendor). */
export const VENDOR_PREQUAL_QUESTIONS = [
  { id: 'years_in_business', question: 'Years in business' },
  { id: 'annual_revenue', question: 'Annual revenue (last fiscal year)' },
  { id: 'emr', question: 'Experience modification rate (EMR)' },
  { id: 'osha_recordables', question: 'OSHA recordable incidents (last 3 years)' },
  { id: 'bonding_capacity', question: 'Bonding capacity (single / aggregate)' },
  { id: 'largest_project', question: 'Largest project completed (value and description)' },
  { id: 'safety_program', question: 'Do you have a written safety program?' },
  { id: 'litigation', question: 'Any pending litigation or claims?' },
  { id: 'references', question: 'Three trade references (name, company, phone)' },
  { id: 'certifications', question: 'Certifications (MBE/WBE/DBE, manufacturer, other)' },
] as const;

export const updateVendorProfileInput = z.object({
  companyId: id,
  csiDivisions: z.array(vendorCsiDivision).max(50).optional(),
  serviceArea: vendorServiceAreaSchema.optional(),
  licenses: z.array(vendorLicenseSchema.extend({ id: id.optional() })).max(50).optional(),
  trades: z.array(z.string().trim().min(1).max(80)).max(50).optional(),
  licenseNumber: optionalText(120),
  expectedVersion: z.number().int().optional(),
});
export type UpdateVendorProfileInput = z.infer<typeof updateVendorProfileInput>;

export const upsertInsuranceCertificateInput = vendorInsuranceCertificateSchema
  .extend({ id: id.optional(), companyId: id })
  .refine((v) => !v.effectiveDate || v.effectiveDate <= v.expiresAt, {
    message: 'Effective date must be before the expiry date',
    path: ['expiresAt'],
  });
export type UpsertInsuranceCertificateInput = z.infer<typeof upsertInsuranceCertificateInput>;

export const removeInsuranceCertificateInput = z.object({ companyId: id, certificateId: id });

export const submitPrequalInput = z.object({
  companyId: id,
  answers: z.array(vendorPrequalAnswerSchema).max(100),
});
export const reviewPrequalInput = z.object({
  companyId: id,
  status: z.enum(['approved', 'rejected', 'pending']),
  note: optionalText(2000),
});

export const rateVendorInput = z.object({
  companyId: id,
  projectId: id.optional().nullable(),
  quality: z.number().int().min(1).max(5),
  schedule: z.number().int().min(1).max(5),
  safety: z.number().int().min(1).max(5),
  communication: z.number().int().min(1).max(5),
  comment: optionalText(2000),
});
export type RateVendorInput = z.infer<typeof rateVendorInput>;

export const INSURANCE_HEALTH = ['missing', 'expired', 'expiring', 'ok'] as const;
export const insuranceHealth = z.enum(INSURANCE_HEALTH);
export type InsuranceHealth = z.infer<typeof insuranceHealth>;

export const listVendorsInput = listInput.extend({
  relationshipType: z.enum(['subcontractor', 'supplier']).optional(),
  trade: z.string().max(80).optional(),
  csiDivision: vendorCsiDivision.optional(),
  /** Matches a service-area region or postal code. */
  serviceArea: z.string().max(120).optional(),
  prequalStatus: prequalStatus.optional(),
  insurance: insuranceHealth.optional(),
  minRating: z.number().int().min(1).max(5).optional(),
});
export type ListVendorsInput = z.infer<typeof listVendorsInput>;

export const expiringInsuranceInput = z.object({
  withinDays: z.number().int().min(0).max(365).default(30),
});

/**
 * `vendor_profile` row: one per sub/supplier company (`company_id` unique). Replaces the
 * `company.custom.__vendor` JSON. Licences, certificates and prequal answers stay JSON arrays
 * (validated by the schemas above); the earliest active GL / workers' comp expiry is denormalized
 * into indexed date columns for expiry alerts and list filters.
 */
export const vendorProfileRecordSchema = baseRow.extend({
  companyId: id,
  trades: z.array(z.string()),
  csiDivisions: z.array(z.string()),
  serviceArea: vendorServiceAreaSchema,
  licenses: z.array(vendorLicenseSchema),
  insuranceCertificates: z.array(vendorInsuranceCertificateSchema),
  prequalAnswers: z.array(vendorPrequalAnswerSchema),
  prequalSubmittedAt: z.date().nullable(),
  prequalReviewedAt: z.date().nullable(),
  prequalReviewedBy: id.nullable(),
  prequalNote: z.string().nullable(),
  /** Average overall score of `vendor_rating` rows (1.00 - 5.00), null when unrated. */
  rating: z.number().nullable(),
  ratingCount: z.number().int(),
  insuranceExpiresAt: z.string().nullable(),
  workersCompExpiresAt: z.string().nullable(),
  /** Last expiry alert sent per certificate id (ISO timestamp), prevents repeat alerts. */
  alertedCertificates: z.record(z.string(), z.string()),
});
export type VendorProfileRecord = z.infer<typeof vendorProfileRecordSchema>;

/** `vendor_rating` row: one scorecard per rating event (optionally tied to a project/commitment). */
export const vendorRatingRecordSchema = baseRow.extend({
  companyId: id,
  projectId: id.nullable(),
  commitmentId: id.nullable(),
  quality: z.number().int(),
  schedule: z.number().int(),
  safety: z.number().int(),
  communication: z.number().int(),
  /** Average of the four scores, two decimals. */
  overall: z.number(),
  comment: z.string().nullable(),
  ratedBy: id.nullable(),
});
export type VendorRatingRecord = z.infer<typeof vendorRatingRecordSchema>;

// ---------------------------------------------------------------------------
// Saved list views
// ---------------------------------------------------------------------------

export const CRM_SAVED_VIEW_ENTITIES = ['contact', 'company', 'deal', 'vendor', 'task'] as const;
export const crmSavedViewEntity = z.enum(CRM_SAVED_VIEW_ENTITIES);
export type CrmSavedViewEntity = z.infer<typeof crmSavedViewEntity>;

export const crmSavedViewFilters = z.record(
  z.string().max(64),
  z.union([z.string().max(500), z.number(), z.boolean(), z.null(), z.array(z.string().max(200)).max(50)]),
);
export type CrmSavedViewFilters = z.infer<typeof crmSavedViewFilters>;

export const createCrmSavedViewInput = z.object({
  entity: crmSavedViewEntity,
  name: nonEmpty(80),
  filters: crmSavedViewFilters,
  shared: z.boolean().default(false),
});
export type CreateCrmSavedViewInput = z.infer<typeof createCrmSavedViewInput>;

export const crmSavedViewSchema = z.object({
  id,
  entity: crmSavedViewEntity,
  name: z.string(),
  filters: crmSavedViewFilters,
  shared: z.boolean(),
  ownerId: id.nullable(),
  createdAt: z.date(),
});
export type CrmSavedView = z.infer<typeof crmSavedViewSchema>;

// ---------------------------------------------------------------------------
// Dedupe + CSV import/export
// ---------------------------------------------------------------------------

export const listDuplicateGroupsInput = z.object({
  entity: z.enum(['contact', 'company']),
  limit: z.number().int().min(1).max(200).default(50),
});

export const csvPreviewInput = z.object({
  entity: z.enum(['contact', 'company']),
  csv: z.string().max(1_000_000).optional(),
  fileKey: z.string().max(1024).optional(),
  mapping: z.record(z.string(), z.string()).optional(),
  sampleSize: z.number().int().min(1).max(50).default(10),
});
export type CsvPreviewInput = z.infer<typeof csvPreviewInput>;

export const CRM_EXPORT_ENTITIES = ['contact', 'company', 'deal', 'vendor'] as const;
export const csvExportInput = z.object({
  entity: z.enum(CRM_EXPORT_ENTITIES),
  search: z.string().trim().max(200).optional(),
  filters: crmSavedViewFilters.default({}),
  includeArchived: z.boolean().default(false),
});
export type CsvExportInput = z.infer<typeof csvExportInput>;

export const crmUploadUrlInput = z.object({
  purpose: z.enum(['import', 'insurance', 'attachment']),
  fileName: z.string().min(1).max(255),
  contentType: z.string().max(255).default('application/octet-stream'),
});

// ---------------------------------------------------------------------------
// Pipelines, deals (extra inputs)
// ---------------------------------------------------------------------------

export const createPipelineFromTemplateInput = z.object({
  businessType,
  /** Index into the business type blueprint's pipelines. */
  templateIndex: z.number().int().min(0).max(20),
  name: z.string().trim().max(120).optional(),
});

export const boardInput = z.object({
  pipelineId: id,
  ownerId: id.optional(),
  search: z.string().trim().max(200).optional(),
  /** Include won/lost deals closed within this many days (default 30). */
  closedWithinDays: z.number().int().min(0).max(3650).default(30),
});
export type BoardInput = z.infer<typeof boardInput>;

// ---------------------------------------------------------------------------
// Estimates + proposals (extra inputs)
// ---------------------------------------------------------------------------

export const reviseEstimateInput = z.object({ id, note: optionalText(500) });
export const setEstimateStatusInput = z.object({ id, status: estimateStatus, expectedVersion: z.number().int().optional() });

export const publicProposalTokenInput = z.object({ token: z.string().min(16).max(256) });

// ---------------------------------------------------------------------------
// On-plan takeoff + estimate line import
// ---------------------------------------------------------------------------

const takeoffFileRef = z.object({
  key: z.string().min(1).max(1024),
  name: z.string().min(1).max(255),
  contentType: z.string().max(255),
  size: z.number().int().nonnegative(),
});

export const takeoffUploadUrlInput = z.object({
  estimateId: id,
  fileName: z.string().min(1).max(255),
  contentType: z.string().max(255).default('application/pdf'),
});

const takeoffPageInput = z.object({
  pageNumber: z.number().int().min(1).max(5000).default(1),
  name: nonEmpty(200),
  /** Page size at render scale 1 (PDF points with /Rotate applied, or image pixels). */
  widthPx: z.number().int().positive().max(100_000).optional().nullable(),
  heightPx: z.number().int().positive().max(100_000).optional().nullable(),
});

/** Sheets from an uploaded PDF/image (one per chosen page) or from a project drawing revision. */
export const createTakeoffSheetsInput = z
  .object({
    estimateId: id,
    file: takeoffFileRef.optional().nullable(),
    drawingRevisionId: id.optional().nullable(),
    pages: z.array(takeoffPageInput).min(1).max(100),
    scale: takeoffScale.optional().nullable(),
  })
  .refine((v) => !!v.file !== !!v.drawingRevisionId, { message: 'Give either an uploaded file or a drawing revision', path: ['file'] });
export type CreateTakeoffSheetsInput = z.infer<typeof createTakeoffSheetsInput>;

export const updateTakeoffSheetInput = z.object({
  id,
  expectedVersion: z.number().int().optional(),
  name: nonEmpty(200).optional(),
  /** Null clears the scale (lengths and areas become 0 until it is set again). */
  scale: takeoffScale.optional().nullable(),
  widthPx: z.number().int().positive().max(100_000).optional().nullable(),
  heightPx: z.number().int().positive().max(100_000).optional().nullable(),
});
export type UpdateTakeoffSheetInput = z.infer<typeof updateTakeoffSheetInput>;

export const reorderTakeoffSheetsInput = z.object({ estimateId: id, ids: z.array(id).min(1).max(500) });
export const archiveTakeoffSheetInput = z.object({ id, archived: z.boolean().default(true) });
export const listTakeoffInput = z.object({ estimateId: id, includeArchived: z.boolean().default(false) });

const takeoffMultiplier = z.number().positive().max(100_000).nullable();

export const createTakeoffMeasurementInput = z.object({
  sheetId: id,
  kind: takeoffKind,
  name: nonEmpty(200),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional().nullable(),
  geometry: takeoffGeometry,
  /** Wall height (linear -> area) or depth (area -> volume) in the sheet scale's unit, or items per count. */
  multiplier: takeoffMultiplier.optional(),
  wasteBps: z.number().int().min(0).max(10_000).default(0),
  /** Output unit (lf, sf, sy, cy, ea, m, m2, m3, ...); defaults from the kind and the scale. */
  unit: z.string().trim().max(20).optional().nullable(),
  costCode: z.string().trim().max(40).optional().nullable(),
});
export type CreateTakeoffMeasurementInput = z.infer<typeof createTakeoffMeasurementInput>;

export const updateTakeoffMeasurementInput = z.object({
  id,
  expectedVersion: z.number().int().optional(),
  name: nonEmpty(200).optional(),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional().nullable(),
  geometry: takeoffGeometry.optional(),
  multiplier: takeoffMultiplier.optional(),
  wasteBps: z.number().int().min(0).max(10_000).optional(),
  unit: z.string().trim().max(20).optional().nullable(),
  costCode: z.string().trim().max(40).optional().nullable(),
  position: z.number().int().min(0).optional(),
});
export type UpdateTakeoffMeasurementInput = z.infer<typeof updateTakeoffMeasurementInput>;

/** Create (or update, when already linked) one estimate line per measurement. */
export const sendTakeoffToEstimateInput = z.object({
  estimateId: id,
  expectedVersion: z.number().int().optional(),
  items: z
    .array(
      z.object({
        measurementId: id,
        description: z.string().trim().min(1).max(1000).optional(),
        unitCostCents: cents.optional(),
        category: costCategory.optional(),
        section: z.string().trim().max(120).optional().nullable(),
      }),
    )
    .min(1)
    .max(200),
});
export type SendTakeoffToEstimateInput = z.infer<typeof sendTakeoffToEstimateInput>;

/**
 * Append CSV rows (parsed in the browser) to an estimate. The API re-validates every row with the
 * same mapping rules; the row cap is enforced by the API with a readable message.
 */
export const importEstimateLinesInput = z.object({
  estimateId: id,
  expectedVersion: z.number().int().optional(),
  headers: z.array(z.string().max(200)).min(1).max(100),
  rows: z.array(z.array(z.string().max(5000)).max(100)).max(20_000),
  /** CSV header -> field (description, quantity, unit, unitCost, costCode, section, category, markup, notes). */
  mapping: z.record(z.string(), z.string()),
  /** Leave out rows that fail validation instead of refusing the whole file. */
  skipInvalid: z.boolean().default(false),
  /** Validate only: report row errors and counts without writing. */
  dryRun: z.boolean().default(false),
});
export type ImportEstimateLinesInput = z.infer<typeof importEstimateLinesInput>;

// ---------------------------------------------------------------------------
// Tasks + "My day"
// ---------------------------------------------------------------------------

export const myDayInput = z.object({
  /** Day to show (`YYYY-MM-DD`) in the viewer's time zone; defaults to today. */
  date: dateOnly.optional(),
  /** IANA time zone of the viewer; defaults to the workspace time zone. */
  timeZone: z.string().max(64).optional(),
  upcomingDays: z.number().int().min(0).max(30).default(7),
});
export type MyDayInput = z.infer<typeof myDayInput>;

// ---------------------------------------------------------------------------
// Email (templates + one-off sends logged to the timeline)
// ---------------------------------------------------------------------------

export const renderCrmTemplateInput = z.object({
  templateId: id.optional(),
  subject: z.string().max(300).optional(),
  body: z.string().max(200_000).optional(),
  contactId: id.optional().nullable(),
  dealId: id.optional().nullable(),
  projectId: id.optional().nullable(),
});
export type RenderCrmTemplateInput = z.infer<typeof renderCrmTemplateInput>;

export const sendCrmEmailInput = z.object({
  contactId: id,
  to: z.array(email).max(10).optional(),
  templateId: id.optional().nullable(),
  subject: nonEmpty(300),
  /** Plain text or simple HTML; merge tags are rendered server-side. */
  body: z.string().min(1).max(200_000),
  dealId: id.optional().nullable(),
  projectId: id.optional().nullable(),
});
export type SendCrmEmailInput = z.infer<typeof sendCrmEmailInput>;

export const listTemplatesInput = z
  .object({ kind: templateKind.optional(), search: z.string().max(200).optional() })
  .default({});

// ---------------------------------------------------------------------------
// Reports (extra outputs)
// ---------------------------------------------------------------------------

export const timeInStageReport = z.array(
  z.object({ stageId: id, stageName: z.string(), avgDays: z.number(), medianDays: z.number(), samples: z.number().int() }),
);
export const dealSizeReport = z.object({
  avgDealCents: cents,
  avgWonDealCents: cents,
  medianWonDealCents: cents,
  openPipelineCents: cents,
  weightedPipelineCents: cents,
});
export const repeatClientReport = z.object({
  clients: z.number().int(),
  repeatClients: z.number().int(),
  repeatRateBps: z.number().int(),
});

